/**
 * DialogBox - DOM-based dialog UI component.
 * Renders at bottom of screen with typewriter text effect.
 *
 * Structure:
 *   .dialog-overlay              (full-screen click catcher)
 *     .dialog-box                (the actual box at the bottom)
 *       .dialog-speaker          (speaker name)
 *       .dialog-text             (typewriter text area)
 *       .dialog-choices          (choice buttons, hidden when not needed)
 *       .dialog-continue         (blinking indicator)
 */
export default class DialogBox {
  static TYPEWRITER_SPEED = 30;      // ms per character
  static FAST_TYPEWRITER_SPEED = 10; // ms when skipping

  constructor() {
    this.element = null;
    this.speakerEl = null;
    this.textEl = null;
    this.choicesEl = null;
    this.continueEl = null;

    // Typewriter state
    this._fullText = '';
    this._currentIndex = 0;
    this._typewriterTimer = null;
    this._isTyping = false;

    // Choice state
    this._choices = null;
    this._selectedChoiceIndex = 0;
    this._onChoiceSelected = null;
    this._onAdvance = null;

    this._visible = false;
    this._onOutsideClick = null;

    this._createElement();
  }

  _createElement() {
    this.element = document.createElement('div');
    this.element.className = 'dialog-overlay';
    this.element.style.display = 'none';

    const box = document.createElement('div');
    box.className = 'dialog-box';

    this.speakerEl = document.createElement('div');
    this.speakerEl.className = 'dialog-speaker';

    this.textEl = document.createElement('div');
    this.textEl.className = 'dialog-text';

    this.choicesEl = document.createElement('div');
    this.choicesEl.className = 'dialog-choices';

    this.continueEl = document.createElement('div');
    this.continueEl.className = 'dialog-continue';
    this.continueEl.textContent = '\u25BC';

    box.appendChild(this.speakerEl);
    box.appendChild(this.textEl);
    box.appendChild(this.choicesEl);
    box.appendChild(this.continueEl);
    this.element.appendChild(box);

    document.body.appendChild(this.element);

    // Click/tap on the box itself to advance dialog
    box.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      this._handleConfirm();
    });

    // Click/tap outside the box (on overlay) to close dialog
    this.element.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      if (this._onOutsideClick) this._onOutsideClick();
    });
  }

  /**
   * Show a text node (no choices).
   * @param {string} speaker
   * @param {string} text
   * @param {Function} onAdvance - Called when player confirms after text finishes
   */
  showText(speaker, text, onAdvance) {
    this._show();
    this.speakerEl.textContent = speaker || '';
    this.speakerEl.style.display = speaker ? 'block' : 'none';
    this._choices = null;
    this._onAdvance = onAdvance;
    this._onChoiceSelected = null;
    this.choicesEl.style.display = 'none';
    this.continueEl.style.display = 'none';

    this._startTypewriter(text);
  }

  /**
   * Show a choice node.
   * @param {string} speaker
   * @param {string} text - Prompt text
   * @param {Array<{label: string}>} choices
   * @param {Function} onChoiceSelected - Called with choice index
   */
  showChoice(speaker, text, choices, onChoiceSelected) {
    this._show();
    this.speakerEl.textContent = speaker || '';
    this.speakerEl.style.display = speaker ? 'block' : 'none';
    this._choices = choices;
    this._selectedChoiceIndex = 0;
    this._onChoiceSelected = onChoiceSelected;
    this._onAdvance = null;
    this.choicesEl.style.display = 'none';
    this.continueEl.style.display = 'none';

    this._startTypewriter(text);
  }

  _startTypewriter(text) {
    this._fullText = text;
    this._currentIndex = 0;
    this._isTyping = true;
    this.textEl.textContent = '';

    this._clearTypewriterTimer();
    this._typeNext();
  }

  _typeNext() {
    if (this._currentIndex >= this._fullText.length) {
      this._isTyping = false;
      this._onTypewriterComplete();
      return;
    }

    this._currentIndex++;
    this.textEl.textContent = this._fullText.substring(0, this._currentIndex);

    // Play dialog blip every 3rd character for retro typewriter feel
    if (this._currentIndex % 3 === 1) {
      window.game?.audioSystem?.audio?.playUiDialog();
    }

    this._typewriterTimer = setTimeout(() => this._typeNext(), DialogBox.TYPEWRITER_SPEED);
  }

  _clearTypewriterTimer() {
    if (this._typewriterTimer !== null) {
      clearTimeout(this._typewriterTimer);
      this._typewriterTimer = null;
    }
  }

  _onTypewriterComplete() {
    if (this._choices) {
      this._renderChoices();
      this.choicesEl.style.display = 'flex';
    } else {
      this.continueEl.style.display = 'block';
    }
  }

  _skipTypewriter() {
    this._clearTypewriterTimer();
    this._currentIndex = this._fullText.length;
    this.textEl.textContent = this._fullText;
    this._isTyping = false;
    this._onTypewriterComplete();
  }

  /**
   * Handle confirm action (space/enter/click).
   */
  _handleConfirm() {
    if (this._isTyping) {
      this._skipTypewriter();
      return;
    }

    if (this._choices) {
      window.game?.audioSystem?.audio?.playUiConfirm();
      if (this._onChoiceSelected) {
        this._onChoiceSelected(this._selectedChoiceIndex);
      }
    } else {
      window.game?.audioSystem?.audio?.playUiClick();
      if (this._onAdvance) {
        this._onAdvance();
      }
    }
  }

  /**
   * Navigate choices with arrow keys.
   * @param {'up'|'down'} direction
   */
  navigateChoice(direction) {
    if (!this._choices || this._isTyping) return;

    const prev = this._selectedChoiceIndex;
    if (direction === 'up') {
      this._selectedChoiceIndex = Math.max(0, this._selectedChoiceIndex - 1);
    } else {
      this._selectedChoiceIndex = Math.min(this._choices.length - 1, this._selectedChoiceIndex + 1);
    }
    if (this._selectedChoiceIndex !== prev) {
      window.game?.audioSystem?.audio?.playUiClick();
    }
    this._updateChoiceHighlight();
  }

  _renderChoices() {
    this.choicesEl.innerHTML = '';
    this._choices.forEach((choice, i) => {
      const choiceEl = document.createElement('div');
      choiceEl.className = 'dialog-choice';
      if (i === this._selectedChoiceIndex) {
        choiceEl.classList.add('selected');
      }

      const arrow = document.createElement('span');
      arrow.className = 'dialog-choice-arrow';
      arrow.textContent = '\u25B6';

      const label = document.createElement('span');
      label.textContent = choice.label;

      choiceEl.appendChild(arrow);
      choiceEl.appendChild(label);

      choiceEl.addEventListener('click', (e) => {
        e.stopPropagation();
        this._selectedChoiceIndex = i;
        this._updateChoiceHighlight();
        if (this._onChoiceSelected) {
          this._onChoiceSelected(i);
        }
      });

      this.choicesEl.appendChild(choiceEl);
    });
  }

  _updateChoiceHighlight() {
    const items = this.choicesEl.querySelectorAll('.dialog-choice');
    items.forEach((el, i) => {
      el.classList.toggle('selected', i === this._selectedChoiceIndex);
    });
  }

  _show() {
    this._visible = true;
    this.element.style.display = 'flex';
  }

  hide() {
    this._visible = false;
    this._clearTypewriterTimer();
    this.element.style.display = 'none';
    this._choices = null;
    this._onAdvance = null;
    this._onChoiceSelected = null;
  }

  isVisible() {
    return this._visible;
  }

  destroy() {
    this._clearTypewriterTimer();
    if (this.element.parentElement) {
      this.element.parentElement.removeChild(this.element);
    }
  }
}
